/** @jsxImportSource @emotion/react */

import { useMutation } from '@apollo/client'
import { FC, useCallback, useState } from 'react'
import { useParams } from 'react-router-dom'
import {
  findOneVideo_findOneVideo,
  findOneVideo_findOneVideo_questions as IQuestion,
} from '__generatedTypes__/findOneVideo'
import { removeVideo, removeVideoVariables } from '__generatedTypes__/removeVideo'
import { updateVideo, updateVideoVariables } from '__generatedTypes__/updateVideo'
import { Revision, Revisions } from 'components/Revisions'
import client from 'configs/apolloClient'
import { FIND_ONE_VIDEOS, REMOVE_VIDEO, UPDATE_VIDEO } from 'queries/video'
import { useHistory } from 'hooks/useHistory'
import { useQuestionsSubmitError } from 'hooks/useQuestionsSubmitError'
import { toast } from 'utils/toast'
import { PlatformSlug } from 'constants/consts'
import * as commonClasses from '../commonStyles'
import { IQuizData, IVideoCategoryProps } from '../types'
import withVideoCategoryContentCheck from '../withVideoCategoryContentCheck'
import VideoQuizForm from './VideoQuizForm'

const VideoQuizEdit: FC<IVideoCategoryProps> = ({ videoCategory, content, redirectToVideoDashboard }) => {
  const { populateHistoryWithUser } = useHistory()
  const { platform = '' } = useParams()

  const initialValues = {
    ...content,
    questions: (content?.questionIds
      ?.map((questionId) => content?.questions?.find(({ id }) => id === questionId))
      .filter((question) => !!question) ?? []) as IQuestion[],
  }
  const [initialsState] = useState<Omit<findOneVideo_findOneVideo, '__typename'>>(initialValues)
  const [updateVideo] = useMutation<updateVideo, updateVideoVariables>(UPDATE_VIDEO)
  const [removeVideo] = useMutation<removeVideo, removeVideoVariables>(REMOVE_VIDEO)
  const { submitQuestionError, setSubmitQuestionError, handleServerError } = useQuestionsSubmitError()

  //TODO Add error handling
  const handleSubmit = useCallback(
    async (fieldsValues: IQuizData) => {
      try {
        const videoUpdate = {
          id: content.id,
          title: fieldsValues.title,
          questionIds: fieldsValues.questions?.map(({ id }) => id),
          questions: fieldsValues.questions?.map(({ id }) => ({ id })),
          history: populateHistoryWithUser(content.history),
        }

        await updateVideo({
          variables: {
            updateVideoInputFieldName: videoUpdate,
          },
        })
        setSubmitQuestionError(null)

        client.writeQuery({
          query: FIND_ONE_VIDEOS,
          variables: { findOneVideoId: content.id },
          data: {
            findOneVideo: { ...content, ...videoUpdate, questions: fieldsValues.questions },
          },
        })
        redirectToVideoDashboard()
        toast.success('Video quiz updated successfully')
      } catch (error) {
        toast.error('Failed to update video quiz')
        handleServerError(error)
      }
    },
    [content, populateHistoryWithUser]
  )

  const handleRemoveResource = useCallback(async () => {
    try {
      await removeVideo({
        variables: {
          removeVideoId: content.id,
        },
      })
      redirectToVideoDashboard()
      toast.success('Video Quiz deleted successfully')
    } catch (error) {
      toast.error('Failed to delete video quiz')
    }
  }, [content])

  return (
    <div css={commonClasses.containerPadding}>
      <VideoQuizForm
        platformSlug={platform as PlatformSlug}
        videoCategory={videoCategory}
        handleSubmit={handleSubmit}
        initialValues={initialsState}
        errorText={submitQuestionError}
        onDeleteClick={handleRemoveResource}
      />
      <Revisions revisions={(content?.history ?? []) as Revision[]} />
    </div>
  )
}

export default withVideoCategoryContentCheck(VideoQuizEdit)
